/* eslint-disable react/prop-types */
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup
} from '@material-ui/core'
import React from 'react'

import { mergeStudents, previewStudentMerge } from 'api'
import constants from 'app/constants'
import { showToast } from 'app/helpers'

const describe = (student) =>
  [
    student?.user?.full_name,
    student?.standard?.name,
    student?.user?.date_of_birth && `DOB ${student.user.date_of_birth}`
  ]
    .filter(Boolean)
    .join(' · ')

const errorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.detail ||
  error?.message ||
  fallback

const note = { fontSize: 13, marginTop: 8 }

/*
 * Pick the profile to keep, preview what moves onto it, then merge. The other
 * selected profiles are detached from the family and soft-deleted on the
 * backend; `onMerged` drops them from the table so a later save of this form
 * does not re-attach them.
 */
export const MergeStudentsDialog = ({ students, onClose, onMerged }) => {
  const [keepUuid, setKeepUuid] = React.useState(null)
  const [preview, setPreview] = React.useState(null)
  const [error, setError] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [merging, setMerging] = React.useState(false)

  React.useEffect(() => {
    setKeepUuid(students?.[0]?.user?.uuid ?? null)
  }, [students])

  const sources = React.useMemo(
    () =>
      (students || [])
        .map(({ user }) => user.uuid)
        .filter((uuid) => uuid !== keepUuid),
    [students, keepUuid]
  )

  React.useEffect(() => {
    if (!keepUuid || !sources.length) return
    let cancelled = false
    setPreview(null)
    setError(null)
    setLoading(true)
    previewStudentMerge({ target: keepUuid, sources })
      .then((data) => !cancelled && setPreview(data))
      .catch(
        (err) => !cancelled && setError(errorMessage(err, 'Preview failed'))
      )
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [keepUuid, sources])

  const handleMerge = async () => {
    setMerging(true)
    setError(null)
    try {
      await mergeStudents({ target: keepUuid, sources })
      showToast('Student profiles merged', constants.SUCCESS)
      onMerged(sources)
      onClose()
    } catch (err) {
      setError(errorMessage(err, 'Merge failed'))
    } finally {
      setMerging(false)
    }
  }

  const counts = Object.entries(preview?.counts || {})

  return (
    <Dialog
      open={!!students}
      onClose={() => !merging && onClose()}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Merge {students?.length} student profiles</DialogTitle>
      <DialogContent>
        <div style={{ fontSize: 14, marginBottom: 8 }}>Keep which profile?</div>
        <RadioGroup
          value={keepUuid}
          onChange={(e) => setKeepUuid(e.target.value)}
        >
          {students?.map((student) => (
            <FormControlLabel
              key={student.user.uuid}
              value={student.user.uuid}
              control={<Radio />}
              label={describe(student)}
              disabled={merging}
            />
          ))}
        </RadioGroup>

        <div style={{ fontSize: 14, marginTop: 12 }}>
          {loading && <CircularProgress size={18} />}
          {preview &&
            (counts.length ? (
              <>
                Moves to the kept profile:
                <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                  {counts.map(([label, count]) => (
                    <li key={label}>
                      {label}: {count}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              'Nothing to move: the other profile has no bookings or records.'
            ))}
        </div>
        {preview?.warnings?.map((warning) => (
          <div key={warning} style={{ ...note, color: '#ed6c02' }}>
            {warning}
          </div>
        ))}
        {preview?.block_reasons?.map((reason) => (
          <div key={reason} style={{ ...note, color: '#d32f2f' }}>
            {reason}
          </div>
        ))}
        {error && <div style={{ ...note, color: '#d32f2f' }}>{error}</div>}
        <div style={{ ...note, color: 'rgba(0, 0, 8, 0.6)' }}>
          The other {sources.length > 1 ? 'profiles are' : 'profile is'} removed
          from this family. This cannot be undone.
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={merging}>
          Cancel
        </Button>
        <Button
          color="secondary"
          variant="contained"
          onClick={handleMerge}
          disabled={!preview?.mergeable || loading || merging}
        >
          {merging ? 'Merging…' : 'Merge'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
